import { writable, get } from 'svelte/store';
import type { Task } from '../types';
import { applyTaskOptimisticUpdate } from '../taskOptimistic';

interface ProjectTasksCacheEntry {
  tasks: Task[];
  loading: boolean;
  fetched: boolean;
  deletedTaskIds?: Set<string>;
}

interface ProjectTasksCache {
  [projectId: string]: ProjectTasksCacheEntry;
}

// Cache for project tasks - shared across all ExpandableProjectChip instances
const cache = writable<ProjectTasksCache>({});

// Track in-flight requests to prevent duplicate fetches
const inFlightRequests = new Map<string, Promise<Task[]>>();

/**
 * Get cached tasks for a project
 */
export function getCachedTasks(projectId: string): {
  tasks: Task[];
  loading: boolean;
  fetched: boolean;
} {
  const c = get(cache);
  return c[projectId] || { tasks: [], loading: false, fetched: false };
}

/**
 * Subscribe to cache changes for a specific project
 */
export function subscribeToCachedTasks(
  projectId: string,
  callback: (data: { tasks: Task[]; loading: boolean; fetched: boolean }) => void,
): () => void {
  return cache.subscribe((c) => {
    callback(c[projectId] || { tasks: [], loading: false, fetched: false });
  });
}

/**
 * Subscribe to cache changes for multiple projects at once (single store subscription).
 * More efficient than calling subscribeToCachedTasks N times, since each individual
 * subscription receives the entire cache object on every change.
 */
export function subscribeToAllCachedTasks(
  callback: (allTasks: Record<string, Task[]>) => void,
): () => void {
  return cache.subscribe((c) => {
    const result: Record<string, Task[]> = {};
    for (const [pid, entry] of Object.entries(c)) {
      if (entry.fetched) {
        result[pid] = entry.tasks;
      }
    }
    callback(result);
  });
}

/**
 * Fetch tasks for a single project (with deduplication)
 */
export async function fetchProjectTasks(projectId: string): Promise<Task[]> {
  const cached = getCachedTasks(projectId);
  if (cached.fetched) {
    return cached.tasks;
  }

  // Check if request is already in-flight
  const existing = inFlightRequests.get(projectId);
  if (existing) {
    return existing;
  }

  // Mark as loading
  cache.update((c) => ({
    ...c,
    [projectId]: {
      tasks: [],
      loading: true,
      fetched: false,
      deletedTaskIds: c[projectId]?.deletedTaskIds || new Set<string>(),
    },
  }));

  const fetchPromise = (async () => {
    try {
      const response = await fetch(`/projects/${projectId}`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (response.ok) {
        const json = await response.json();
        const tasks = json.tasks || [];
        let cachedTasks: Task[] = tasks;
        cache.update((c) => {
          const entry = c[projectId];
          const deletedTaskIds = entry?.deletedTaskIds || new Set<string>();
          cachedTasks = mergeWithNewerCachedTasks(tasks, entry?.tasks || [], deletedTaskIds);
          return {
            ...c,
            [projectId]: { tasks: cachedTasks, loading: false, fetched: true, deletedTaskIds },
          };
        });
        return cachedTasks;
      }
      throw new Error('Failed to fetch tasks');
    } catch (e) {
      console.error('Failed to load tasks for project:', projectId, e);
      cache.update((c) => {
        const deletedTaskIds = c[projectId]?.deletedTaskIds || new Set<string>();
        return {
          ...c,
          [projectId]: { tasks: [], loading: false, fetched: true, deletedTaskIds },
        };
      });
      return [];
    } finally {
      inFlightRequests.delete(projectId);
    }
  })();

  inFlightRequests.set(projectId, fetchPromise);
  return fetchPromise;
}

/**
 * Batch fetch tasks for multiple projects in a single HTTP request.
 * Falls back to individual fetches if the batch endpoint fails.
 */
export async function batchFetchProjectTasks(projectIds: string[]): Promise<void> {
  // Filter to only projects we haven't fetched yet
  const toFetch = projectIds.filter((id) => {
    const cached = getCachedTasks(id);
    return !cached.fetched && !inFlightRequests.has(id);
  });

  if (toFetch.length === 0) return;

  // Mark all as loading
  cache.update((c) => {
    const updates = { ...c };
    for (const id of toFetch) {
      updates[id] = {
        tasks: [],
        loading: true,
        fetched: false,
        deletedTaskIds: c[id]?.deletedTaskIds || new Set<string>(),
      };
    }
    return updates;
  });

  try {
    const response = await fetch('/batch_tasks', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token':
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({ project_ids: toFetch }),
    });

    if (response.ok) {
      const data: Record<string, Task[]> = await response.json();
      cache.update((c) => {
        const updates = { ...c };
        for (const id of toFetch) {
          const entry = c[id];
          const deletedTaskIds = entry?.deletedTaskIds || new Set<string>();
          updates[id] = {
            tasks: mergeWithNewerCachedTasks(data[id] || [], entry?.tasks || [], deletedTaskIds),
            loading: false,
            fetched: true,
            deletedTaskIds,
          };
        }
        return updates;
      });
      return;
    }
    // Fall through to individual fetches on non-OK response
  } catch {
    // Fall through to individual fetches on network error
  }

  // Fallback: fetch individually
  await Promise.all(toFetch.map((id) => fetchProjectTasks(id)));
}

/**
 * Clear cache for a specific project (e.g., after task mutation)
 */
export function invalidateProjectTasks(projectId: string): void {
  cache.update((c) => {
    const newCache = { ...c };
    delete newCache[projectId];
    return newCache;
  });
}

/**
 * Clear entire cache
 */
export function clearTasksCache(): void {
  cache.set({});
}

// =============================================================================
// Task Mutation Functions (for syncing after ProjectRail mutations)
// =============================================================================

/**
 * Directly set tasks for a project (used after mutations in ProjectRail)
 */
function taskTimestamp(task: Task): number {
  const timestamp = Date.parse(task.updatedAt || '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function mergeWithNewerCachedTasks(
  incoming: Task[],
  existing: Task[],
  deletedTaskIds: Set<string> = new Set(),
): Task[] {
  const existingById = new Map(existing.map((task) => [task.id, task]));

  return incoming
    .filter((incomingTask) => !deletedTaskIds.has(incomingTask.id))
    .map((incomingTask) => {
      const existingTask = existingById.get(incomingTask.id);
      if (!existingTask) return incomingTask;

      return taskTimestamp(existingTask) > taskTimestamp(incomingTask)
        ? existingTask
        : incomingTask;
    });
}

export function setProjectTasks(projectId: string, tasks: Task[]): void {
  cache.update((c) => {
    const entry = c[projectId];
    const existing = entry?.tasks || [];
    const deletedTaskIds = entry?.deletedTaskIds || new Set<string>();
    return {
      ...c,
      [projectId]: {
        tasks: mergeWithNewerCachedTasks(tasks, existing, deletedTaskIds),
        loading: false,
        fetched: true,
        deletedTaskIds,
      },
    };
  });
}

/**
 * Update a single task in the cache
 */
export function updateTaskInCache(projectId: string, taskId: string, updates: Partial<Task>): void {
  cache.update((c) => {
    const entry = c[projectId];
    if (!entry) return c;
    const deletedTaskIds = new Set(entry.deletedTaskIds);
    deletedTaskIds.delete(taskId);
    return {
      ...c,
      [projectId]: {
        ...entry,
        deletedTaskIds,
        tasks: entry.tasks.map((t) =>
          t.id === taskId ? applyTaskOptimisticUpdate(t, updates) : t,
        ),
      },
    };
  });
}

/**
 * Add a task to the cache
 */
export function addTaskToCache(projectId: string, task: Task): void {
  cache.update((c) => {
    const entry = c[projectId] || { tasks: [], loading: false, fetched: true };
    const deletedTaskIds = new Set(entry.deletedTaskIds);
    deletedTaskIds.delete(task.id);
    return {
      ...c,
      [projectId]: {
        ...entry,
        deletedTaskIds,
        tasks: [...entry.tasks, task],
      },
    };
  });
}

/**
 * Confirm one optimistic creation against the current list, not the list from
 * when its request started. Other pending/saved tasks and edits stay intact.
 * A refetch may already contain the saved row: keep it exactly once.
 * Preserve fetched/loading so an invalidated cache still gets a full refetch.
 */
export function confirmTaskCreation(projectId: string, tempId: string, created: Task): void {
  cache.update((c) => {
    const entry = c[projectId];
    if (!entry) return c; // Invalidated while saving; the next fetch includes the saved task.
    const existing = entry.tasks.find((task) => task.id === created.id);
    const confirmed =
      existing && taskTimestamp(existing) > taskTimestamp(created) ? existing : created;
    const deleted = entry.deletedTaskIds?.has(created.id);
    const hasTemp = entry.tasks.some((task) => task.id === tempId);
    const tasks = entry.tasks
      .filter((task) => task.id !== created.id && (!deleted || task.id !== tempId))
      .map((task) => (task.id === tempId ? confirmed : task));
    if (!hasTemp && !deleted) tasks.push(confirmed);
    return { ...c, [projectId]: { ...entry, tasks } };
  });
}

/**
 * Remove a task from the cache
 */
export function removeTaskFromCache(projectId: string, taskId: string): void {
  cache.update((c) => {
    const entry = c[projectId];
    if (!entry) return c;
    const deletedTaskIds = new Set(entry.deletedTaskIds);
    deletedTaskIds.add(taskId);
    return {
      ...c,
      [projectId]: {
        ...entry,
        deletedTaskIds,
        tasks: entry.tasks.filter((t) => t.id !== taskId),
      },
    };
  });
}

/**
 * Move a task between project caches without refetching.
 *
 * Only adds the task to the target project if that target cache has already been
 * fetched. This avoids inventing an incomplete cache entry for views that have
 * never loaded the target project, while keeping visible project cards in sync.
 */
export function moveTaskBetweenProjectCaches(
  sourceProjectId: string,
  targetProjectId: string,
  task: Task,
): void {
  cache.update((c) => {
    const next = { ...c };
    const movedTask = { ...task, projectId: targetProjectId };

    const sourceEntry = next[sourceProjectId];
    if (sourceEntry) {
      next[sourceProjectId] = {
        ...sourceEntry,
        tasks: sourceEntry.tasks.filter((t) => t.id !== task.id),
      };
    }

    const targetEntry = next[targetProjectId];
    if (targetEntry?.fetched) {
      next[targetProjectId] = {
        ...targetEntry,
        tasks: [...targetEntry.tasks.filter((t) => t.id !== task.id), movedTask],
      };
    }

    return next;
  });
}

// =============================================================================
// TaskCounts Computation (for syncing counts to ProjectCard, tooltips, etc.)
// =============================================================================

export interface TaskCounts {
  total: number;
  done: number;
  todo: number;
}

/**
 * Compute taskCounts from cached tasks
 * Returns null if project is not in cache
 */
export function computeTaskCounts(projectId: string): TaskCounts | null {
  const entry = get(cache)[projectId];
  if (!entry || !entry.fetched) return null;

  const tasks = entry.tasks;
  const done = tasks.filter((t) => t.status !== 'todo').length;
  const todo = tasks.filter((t) => t.status === 'todo').length;
  return { total: tasks.length, done, todo };
}

/**
 * Subscribe to computed taskCounts for a project
 * Callback receives null if project is not in cache
 */
export function subscribeToTaskCounts(
  projectId: string,
  callback: (counts: TaskCounts | null) => void,
): () => void {
  return cache.subscribe((c) => {
    const entry = c[projectId];
    if (!entry || !entry.fetched) {
      callback(null);
      return;
    }
    const tasks = entry.tasks;
    const done = tasks.filter((t) => t.status !== 'todo').length;
    const todo = tasks.filter((t) => t.status === 'todo').length;
    callback({ total: tasks.length, done, todo });
  });
}
